<template>
  <div h-scroll v-bind:key="refreshKey">
    <slot name="table-top-actions" v-bind:selectedIds="selectedIds"></slot>
    <!--    <b-button @click="createExcelFile" class="d-inline-block" size="sm" variant="outline-success">-->
    <!--      Export this page to excel-->
    <!--    </b-button>-->
    <!-- === Table Header Summary === -->
    <div class="d-flex justify-content-between align-items-center mb-1">
      <div class="font-weight-bold">
        Total Clients:
        <span class="text-primary">{{ pagination.total }}</span>
      </div>

    </div>

    <!-- Top pagination -->
    <table-pagination
        class="mb-1"
        :pagination="pagination"
        :sizes="[25, 50, 100, 250, 500]"
        :counter-label="$t('columns.counterNames.clients')"
        @page-change="onPaginationPage"
        @size-change="onPaginationSize"
    />

    <b-table ref="clientTable" v-handy-scroll responsive v-bind:items="rows" v-bind:fields="fields" no-local-sorting
             v-bind:sort-by.sync="sort.field" v-bind:sort-desc.sync="sortIsDesc"
             striped @row-clicked="onRowClicked" hover v-bind:busy="loading"
             @sort-changed="onSortChanged"
             style="white-space: nowrap; padding-bottom: 30px"
    >
      <template #head(selection)="data">
        <div class="no-table-click">
          <b-form-checkbox v-bind:indeterminate="isIndeterminate(selected)" v-model="selected.all"
                           style="margin-top: 7px"
                           @input="val=>toggleAllRow(val)" inline
          ></b-form-checkbox>
        </div>
      </template>

      <!--Cell status-->
      <template #cell(statusId)="data" class="p-0">
        <div class="no-table-click" style="white-space: nowrap; min-width: 200px">
          <v-select class="small"
                    v-bind:value="rows[data.index].statusId"
                    v-bind:options="statusOptions"
                    :reduce="s => s.key"
                    label="name"
                    :clearable="false"
                    :appendToBody="true"
                    @input="status => saveStatus(data.item.id, status)"
          >
            <template #selected-option="option">
              <div :style="{ color: option.color }">
                {{ option.name }}
              </div>
            </template>
            <template #option="option">
              <div :style="{ color: option.color }">
                {{ option.name }}
              </div>
            </template>
          </v-select>
        </div>
      </template>


      <template #cell(representativeUserId)="data">
        <user-picker v-bind:value="rows[data.index].representativeUserId" v-bind:org-id="data.item.orgId"
                     small appendToBody
                     :style="{minWidth: '200px'}"
                     :clientId="data.item.id"
                     :user-type="1"
                     :disabled="!hasPermission(permissions.CLIENT_UPDATE_REPRESENTATIVE)"
                     @update="saveClientUser"
        />
      </template>

      <template #cell(mobile)="data">
        <span v-if="hasPermission(permissions.CLIENT_READ_PHONE)" class="clickable-phone"
              @click.stop="copyToClipboard(data.value)"
        >{{ data.value }}</span>
        <span v-else>xxxxxx</span>
      </template>

      <template #cell(phone)="data">
        <span v-if="hasPermission(permissions.CLIENT_READ_PHONE)" class="clickable-phone"
              @click.stop="copyToClipboard(data.value)"
        >{{ data.value }}</span>
        <span v-else>xxxxxx</span>
      </template>


      <template #cell(email)="data">
        <span v-if="hasPermission(permissions.CLIENT_READ_EMAIL)">{{ data.value}}</span>
        <span v-else>xxxxxx</span>
      </template>
      <template #cell(clientCell)="data">
        <ClientCell :client="data.item" @click.native.stop />
      </template>

      <template #cell(ftdOwnerId)="data">
        {{ getUserFullName(data.value) }}
      </template>

      <template #cell(stdOwnerId)="data">
        {{ getUserFullName(data.value) }}
      </template>

      <template #cell(lastNote)="data">
        <div
            v-if="data.value != null && data.value !== ''"
            style="max-width: 250px; overflow: hidden; text-overflow: ellipsis;"
        >
          <user-avatar :user-id="data.value.userId" size="30" class="mr-1" />

          <div class="d-inline-block align-middle">
      <span
          :id="`${data.value.id}`"
          :title="data.value.text && data.value.text.length > 10 ? data.value.text : null"
          class="d-block font-weight-bold"
      >
        {{ data.value.text }}
      </span>
            <small class="text-muted">
              {{ data.value.time | momentDateTime }}
            </small>
          </div>
        </div>
      </template>

      <template #cell(lastNoteTime)="data">
        <div v-if="data.item.lastNote != null && data.item.lastNote !== ''">
          {{ data.item.lastNote.time | momentDateTime }}
        </div>
      </template>

      <template #cell(lastNoteText)="data">
        <div v-if="data.item.lastNote != null && data.item.lastNote !== ''">
          {{ data.item.lastNote.text }}
        </div>
      </template>

      <template #cell(orgId)="data">
        <span :style="{ color: getOrganizationColorById(data.value), fontWeight: getOrganizationColorById(data.value) ? 'bold' : 'normal' }">
          {{ getOrganizationNameById(data.value) }}
        </span>
      </template>

      <template #cell(account.balance)="data">
        <span v-if="data.item.account == null || data.item.account.currency == null">{{data.value}}</span>
        <span v-else>{{ convertToCurrency(data.value, data.item.account.currency) }}</span>
      </template>
      <template #cell(account.equity)="data">
        <span v-if="data.item.account == null || data.item.account.currency == null">{{data.value}}</span>
        <span v-else>{{ convertToCurrency(data.value, data.item.account.currency) }}</span>
      </template>

      <template #cell(country)="data">
        {{ data.value }}
      </template>
      <template #cell(ftdAmount)="data">
        <span style="font-weight: bold" v-if="data.item.account == null || data.item.account.currency == null">{{data.value}}</span>
        <span style="font-weight: bold" v-else>{{ convertToCurrency(data.value, data.item.account.currency) }}</span>
      </template>

      <template #cell(businessUnitId)="data">
        {{ getBusinessUnitNameById(data.value) }}
      </template>

      <template #cell(selection)="data">
        <b-form-checkbox v-model="selectedIds[data.item.id]" inline @input="val=>toggleRow(data.item, val)"
        ></b-form-checkbox>
      </template>
      <template #cell(monetary.totalDeposits)="data">
        <span v-if="data.item.account == null || data.item.account.currency == null">{{data.value}}</span>
        <span v-else>{{ convertToCurrency(data.value, data.item.account.currency) }}</span>
      </template>
      <template #cell(monetary.totalWithdrawals)="data">
        <span v-if="data.item.account == null || data.item.account.currency == null">{{data.value}}</span>
        <span v-else>{{ convertToCurrency(data.value, data.item.account.currency) }}</span>
      </template>
      <template #cell(hasAccounts)="data">
        {{ data.value ? $t('operations.yes') : $t('operations.no') }}
      </template>
    </b-table>
    <!-- Bottom pagination -->
    <table-pagination
        class="mt-2"
        :pagination="pagination"
        :sizes="[25, 50, 100, 250, 500]"
        :counter-label="$t('columns.counterNames.clients')"
        @page-change="onPaginationPage"
        @size-change="onPaginationSize"
    />

  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinTable from '@/components/mixin/mixinTable';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinBase from '@/components/mixin/mixinBase';
import UserAvatar from '@/components/widget/UserAvatar';
import ClientCell from '@/components/partial/clients/ClientCell';
import ClientTimeZone from '@/components/widget/utils/clientTimeZone';
import { vue } from '@/main';
import UserPicker from '@/components/widget/picker/userPicker.vue';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import UserSelectBox from '@/components/widget/select/UserSelectBox.vue';
import TablePagination from '@/components/widget/TablePagination.vue';

const tdClassMap = {
  'statusId': ['px-1', 'no-click'],
  'presaleUserId': ['px-1', 'no-click'],
  'representativeUserId': ['px-1', 'no-click'],
  'retentionUserId': ['px-1', 'no-click']
};

export default {
  name: 'ClientsTable',
  components: {
    TablePagination,
    UserSelectBox,
    UserPicker,
    ClientTimeZone,
    UserAvatar,
    ClientCell
  },
  mixins: [mixinBase, mixinTable, mixinNotifications, mixinPermissions],
  data: () => ({
    rows: [],
    selected: { all: false },
    exportingInProgress: false,
  }),
  props: {
    filters: {
      default: () => ({})
    },
    columns: {
      default: () => ([])
    },
    hasSelection: {
      default: () => false
    },
    busy: {
      default: () => false
    },
    defaultPaginationSize: {
      type: Number,
      default: 25
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.pagination.page = 1;
        this.refresh();
      }
    }
  },
  created() {
    console.log('created');
    this.setPagination(1, 0, this.defaultPaginationSize || 25);
    this.refresh();
  },
  mounted() {

    // setInterval(this.updateTimes, 1000); // Update times every second

  },
  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById', 'getOrganizationColorById', 'getUserFullName', 'getAffiliateNameById']),
    ...mapGetters('clients', ['allStatuses']),

    statusOptions() {
      return this.allStatuses.map(status => {
        return {
          key: status.id,
          name: status.status,
          color: status.color
        };
      });

    },
    fields() {
      let $this = this;

      let fields = (this.columns || []).map(column => {
        let columnName = column.key.includes('.') ? column.key.substring(column.key.lastIndexOf('.') + 1) : column.key;
        return {
          key: column.key,
          label: $this.$t(`columns.${columnName}`),
          sortable: true,
          tdClass: tdClassMap[column.key],
          active: column.enable,
          thStyle: column.width != null && column.width !== '' ?  `min-width: ${column.width}px`  : '',
          class: column.width  != null && column.width !== '' ? 'text-center': '',
        };
      });
   /*    fields.push({
        key: 'countryTime',
        label: 'countryTime',
        class: 'text-center',
        sortable: false,
        active: true,
      }); */
      fields = [...fields]
          .sort((a, b) => {
            if (a.key === 'id') return -1; // Move 'type' field to the second position
            if (b.key === 'id') return 1;
            return 0;
          });
      if (this.hasSelection) {
        fields.unshift({
          key: 'selection',
          label: '',
          sortable: false,
          active: true,
        });
      }
      return fields.filter(f => f.active === true);
    },
    selectedIds() {
      this.refreshKey; // Little hack to recompile
      // console.log("selectedIds()",this.selected)
      return Object.keys(this.selected)
          .filter(k => k !== 'all')
          .reduce((ans, id) => Object.assign(ans, { [id]: true }), {});
    },
    userTypeByField() {
      return {
        1: 'Representative',
        2: 'Retention',
        3: 'Presale',
      };
    },
  },
  methods: {
    ...mapActions('clients', ['getAllClients', 'exportClientDataExcel', 'updateClientStatus', 'updateClientUsers']),
    onPaginationPage(page) {
      this.pagination.page = page;
      this.refresh();
    },
    onPaginationSize(size) {
      this.pagination.size = size;
      this.pagination.page = 1;
      this.refresh();
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$bvToast.toast(`Copied: ${text}`, {
          title: 'Phone number copied',
          variant: 'success',
          autoHideDelay: 2000,
          solid: true
        });
      } catch (err) {
        console.error('Failed to copy:', err);
        this.$bvToast.toast('Failed to copy', {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 2000,
          solid: true
        });
      }
    },
    async refresh() {
      let $this = this;

      if (this.loading || this.busy) {
        return;
      }

      let loadingToken = this.setLoading();

      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'creationTime';
      }

      const requestPayload = {
        pagination: this.pagination,
        sort,
        filter: this.getFilterForRequest(),
      };


      const result = await this.getAllClients(requestPayload)
          .catch(err => {
            $this.showErrorNotification(
                $this.$t('globalMessages.fetchDataError', { system: $this.$t('menu.clients') }),
                $this.getErrorDescription(err)
            );
            $this.setLoaded(loadingToken);
          });

      if (result) {

        this.rows = result.data;
        this.setMetaDataFromResult(result);
      } else {
        console.warn('⚠️ No result received (likely due to error)');
      }

      this.setLoaded(loadingToken);
    },
    getFilterForRequest(){
      // Transform the filters to match ClientSearchRequest format
      let transformedFilters = { ...this.filters };

      // Handle creationTime transformation
      if (transformedFilters.creationTime) {
        if (transformedFilters.creationTime.start) {
          transformedFilters.creationTimeStart = transformedFilters.creationTime.start;
        }
        if (transformedFilters.creationTime.end) {
          transformedFilters.creationTimeEnd = transformedFilters.creationTime.end;
        }
        delete transformedFilters.creationTime;
      }

      // Handle ftdTime transformation
      if (transformedFilters.ftdTime) {
        if (transformedFilters.ftdTime.start) {
          transformedFilters.ftdTimeStart = transformedFilters.ftdTime.start;
        }
        if (transformedFilters.ftdTime.end) {
          transformedFilters.ftdTimeEnd = transformedFilters.ftdTime.end;
        }
        delete transformedFilters.ftdTime;
      }
      return transformedFilters
    },
    saveStatus(id, status) {
      let $this = this;
      let loadingToken = this.setLoading();
      this.updateClientStatus({
        clientId: id,
        statusId: status
      })
          .then(client => {
            $this.updateClient(client);
            this.showSuccessNotification('Status updated successfully');
          })
          .catch(err => {
            this.showErrorNotification('Failed to update status', this.getErrorDescription(err));
          })
          .finally(() => {
            this.setLoaded(loadingToken);
          });
    },
    saveClientUser({
      clientId,
      userId,
      userType
    }) {
      let $this = this;
      let loadingToken = this.setLoading();
      console.log('changeClientUser', clientId, userId, userType);
      this.updateClientUsers({
        clientId: clientId,
        userId: userId,
        userType: userType
      })
          .then(client => {
            $this.updateClient(client);
            this.showSuccessNotification(this.userTypeByField[userType] + ' updated successfully');
          })
          .catch(err => {
            this.showErrorNotification('Failed to update ' + this.userTypeByField[userType], this.getErrorDescription(err));
          })
          .finally(() => {
            this.setLoaded(loadingToken);
          });
    },
    updateClient(client) {
      let clientIndex = this.rows.findIndex(c => c.id === client.id);
      this.rows[clientIndex] = client;
    },
    getAllSelected() {
      return this.selected;
    },
    createExcelFile() {
      let $this = this;
      if ($this.exportingInProgress) {
        this.showWarningNotification('Exporting data to Excel is already in progress. Please wait...');
        return;
      }
      this.showInfoNotification('Exporting data to Excel...');
      this.exportingInProgress = true;
      let sort = { ...$this.sort };
      if (sort.field == null) {
        sort.field = 'creationTime';
      }
      this.exportClientDataExcel({
        pagination: this.paginationExcel,
        sort,
        filter: this.getFilterForRequest(),
      })
          .finally(() => {
            $this.exportingInProgress = false;
          });
    },
  }
};
</script>

<style scoped>
.clickable-phone {
  cursor: pointer;
  color: #007bff;
  text-decoration: underline;
  transition: all 0.2s ease;
}

.clickable-phone:hover {
  color: #0056b3;
  text-shadow: 0 0 4px rgba(0, 123, 255, 0.4);
}

</style>
